import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@my-saas/ui";
import { Helmet } from "react-helmet-async";

import { useEngineStatus } from "@/client/services/platform";

export const PlatformEnginePage = () => {
  const { status, loading } = useEngineStatus();

  return (
    <>
      <Helmet>
        <title>Platform · Engine status</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Alert engine status</h1>
          <p className="text-sm text-muted-foreground">
            Live snapshot of the OREF HTTP poller, MQTT listeners, and Tzevaadom WS feed. Refreshes every 5s.
          </p>
        </div>

        {loading && !status && <p className="text-sm text-muted-foreground">Loading…</p>}

        {status && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Tenants loaded</CardTitle>
                <CardDescription>Tenants currently in the in-process runtime registry.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-mono">{status.tenants.loaded}</div>
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {status.tenants.ids.map((id) => (
                    <li key={id} className="font-mono">{id}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  OREF HTTP poller
                  <Badge variant={status.sources.orefHttp.enabled ? "primary" : "error"}>
                    {status.sources.orefHttp.enabled ? "enabled" : "disabled"}
                  </Badge>
                </CardTitle>
                <CardDescription>10s cron against alerts.json + history endpoints.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Last poll: </span>
                  <span className="font-mono text-xs">{status.sources.orefHttp.lastPollAt ?? "never"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last alert count: </span>
                  <span className="font-mono">{status.sources.orefHttp.lastPollAlertCount}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="text-xs text-muted-foreground mb-1">Source failures:</div>
                  {Object.entries(status.sources.orefHttp.failures).map(([k, v]) => (
                    <div key={k} className="text-xs flex justify-between">
                      <span className="font-mono">{k}</span>
                      <span className={v.consecutiveFailures > 0 ? "text-destructive" : "text-emerald-600"}>
                        {v.consecutiveFailures > 0 ? `${v.consecutiveFailures} fails` : "ok"}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  OREF MQTT
                  <Badge variant={status.sources.orefMqtt.enabled ? "primary" : "error"}>
                    {status.sources.orefMqtt.enabled ? "enabled" : "disabled"}
                  </Badge>
                </CardTitle>
                <CardDescription>Pushy-bridged listeners against ioref.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="text-xs grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-muted-foreground">received</div>
                    <div className="font-mono">{status.sources.orefMqtt.receivedCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">parsed</div>
                    <div className="font-mono">{status.sources.orefMqtt.parsedCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">errors</div>
                    <div className="font-mono">{status.sources.orefMqtt.parseErrorCount}</div>
                  </div>
                </div>
                <div className="border-t pt-2 mt-2 space-y-1">
                  {status.sources.orefMqtt.listeners.map((l) => (
                    <div key={l.index} className="text-xs flex justify-between gap-2">
                      <span className="font-mono">listener {l.index}</span>
                      <span className={l.connected ? "text-emerald-600" : "text-destructive"}>
                        {l.connected ? "connected" : "disconnected"}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};
